import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomTableContainer from "views/Tables/CustomTableContainer";
import { debounce } from "lodash";
import { CustomSearch, CustomSwitch } from "shared/AllInputs";
import CustomButton from "shared/CustomButton";
import CustomOverlay from "shared/CustomOverlay";
import CustomCard from "shared/CustomCard";
import {
  getRentsetupListAction,
  rentSetupActiveInactiveAction,
} from "store/actions/rentsetupActions";
import ArchiveIcon from "assets/images/icons/archive.svg";
import { showToastAction } from "store/slices/commonSlice";
import CustomArchiveModal from "shared/Modals/CustomArchiveModal";
import { setRentSetupSlice } from "store/slices/RentSetupSlices";
import { CustomTooltipView } from "shared/AllViewInputs";

const RentSetupList = () => {
  const openAction = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [rentSetupsList, setRentsetupsList] = useState([]);
  const [first, setFirst] = useState(0);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const page = query.get("page") || 1;
  const rows = query.get("rows") || 10;
  const [count, setCount] = useState(0);
  const [isSearch, setIsSearch] = useState(false);
  const { rentSetup } = useSelector((state) => state?.rentSetup);
  // const [columns, setColumns] = useState([]);
  const [isArchive, setIsArchive] = useState(false);
  const [idToArchive, setIdToArchive] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSwitch, setIsSwitch] = useState(false);
  const [baseStatus, setBaseStatus] = useState("active");
  const [selectedItems, setSelectedItems] = useState([]);

  useMemo(() => {
    setFirst(page * rows - rows);
  }, []);

  const header = () => {
    return (
      <div className="w-12 md:w-9 flex justify-content-end flex-wrap align-items-center">
        <div className="w-12 md:w-8 mr-3">
          <CustomSearch
            name="search"
            value={search}
            onChange={({ value }) => {
              setSearch(value);
              setFirst(0);
              debouncedOnChange.current(rows, page, value, baseStatus);
            }}
            isSearch={isSearch}
            placeholder="Search Rent Setup"
          />
        </div>
        <CustomButton
          size="small"
          icon="pi pi-plus"
          extraClassNames="mr-3"
          label="Add Rent Regulation"
          onClick={() => navigate("/rent/add")}
        />
        <CustomOverlay>
          <ul className="no-style p-0">
            {baseStatus === "archived" ? (
              <>
                <li
                  className="flex gap-2 text-xs font-medium mb-3 cursor-pointer"
                  onClick={() => {
                    setBaseStatus("");
                    setSelectedItems([]);
                    getRentsetupsList(rows, page, search, "active");
                  }}
                >
                  <img src={ArchiveIcon} />
                  Hide Archived
                </li>
                <li
                  className="flex gap-2 text-xs font-medium mb-3 cursor-pointer"
                  onClick={() => {
                    archiveUnarchiveMultiple(selectedItems, "restore");
                  }}
                >
                  <img src={ArchiveIcon} />
                  {`Unarchive (${selectedItems?.length || 0})`}
                </li>
              </>
            ) : (
              <>
                <li
                  className="flex gap-2 text-xs font-medium mb-3 cursor-pointer"
                  onClick={() => {
                    setBaseStatus("archived");
                    setSelectedItems([]);
                    getRentsetupsList(rows, page, search, "archived");
                  }}
                >
                  <img src={ArchiveIcon} />
                  Show Archived
                </li>
                <li
                  className="flex gap-2 text-xs font-medium mb-3 cursor-pointer"
                  onClick={() => {
                    archiveUnarchiveMultiple(selectedItems, "archived");
                  }}
                >
                  <img src={ArchiveIcon} />
                  {`Archive (${selectedItems?.length || 0})`}
                </li>
              </>
            )}
          </ul>
        </CustomOverlay>
      </div>
    );
  };

  const actions = (d) => {
    return (
      <CustomOverlay extraClass={"text-center"}>
        <ul className="no-style p-0">
          <li
            className="flex gap-2 text-xs font-medium mb-3 cursor-pointer"
            onClick={() => {
              navigate(`/rent/view/${d.id}`);
              dispatch(setRentSetupSlice(d));
            }}
          >
            <i className="pi pi-eye cursor-pointer text-dark"></i>
            View
          </li>
          <li
            className="flex gap-2 text-xs font-medium mb-3 cursor-pointer"
            onClick={() => {
              setIdToArchive({
                id: [d?.id],
                action: d?.base_status === "archived" ? "restore" : "archived",
              });
              setIsArchive(true);
            }}
          >
            <img src={ArchiveIcon} />
            {d?.base_status === "archived" ? "Unarchive" : "Archive"}
          </li>
        </ul>
      </CustomOverlay>
    );
  };

  const ActionTemplate = () => {
    return (
      <>
        <p
          className="flex gap-2 align-items-center"
        // onClick={(e) => openAction.current.toggle(e)}
        >
          {/* <img src={manageColICon} style={{ width: "27px" }} /> */}
          {/* <i className="pi pi-chevron-down"></i> */}
          Actions
        </p>
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        name: "Regulation Name",
        accessor: "regulation_name",
        value: true,
        frozen: true,
        filter: false,
      },
      {
        name: "Federal State",
        accessor: "federal_state",
        value: true,
        filter: false,
      },
      {
        name: "Note",
        accessor: "note",
        value: true,
        filter: false,
        body: (options) => (
          <CustomTooltipView
            value={options?.note}
            id={options?.id}
            size={30}
            isSelected={selectedItems.some((item) => item?.id === options?.id)}
          />
        ),
      },
      {
        name: "Status",
        accessor: "base_status",
        value: true,
        filter: false,
        body: (options) => (
          <CustomSwitch
            value={options?.is_status}
            onChange={({ value }) => handleSetupUpdate(options?.id, value)}
            disabled={isSwitch}
          />
        ),
      },
    ],
    [rentSetupsList, selectedItems]
  );

  const handleSetupUpdate = (id, value) => {
    dispatch(
      rentSetupActiveInactiveAction(id, value, dispatch, setIsSwitch, (res) => {
        getRentsetupsList(rows, page, search);
      })
    );
  };

  // const [columnOptions, setColumnOptions] = useState(columnOptionsInitial);

  const getRentsetupsList = (rows, page, search, baseStatus) => {
    dispatch(
      getRentsetupListAction(rows, page, search, baseStatus, (res) => {
        setRentsetupsList(res?.list);
        setCount(res?.count);
        setIsSearch(false);
      })
    );
  };

  const handleSearch = (rows, page, search, baseStatus) => {
    setIsSearch(true);
    getRentsetupsList(rows, 1, search, baseStatus);
  };

  const debouncedOnChange = useRef(debounce(handleSearch, 1000));

  const onPageChange = (e) => {
    navigate(`/rent/list?page=${e.page + 1}&rows=${e.rows}`);
    getRentsetupsList(e.rows, e.page + 1, search, baseStatus);
    setFirst(e.first);
  };

  useEffect(() => {
    getRentsetupsList(rows, page, "", baseStatus);
  }, []);

  // useEffect(() => {
  //     let filterdCols = columnOptions.filter((col) => col.value === true);
  //     setColumns(filterdCols);
  // }, []);

  const handleArchiveUnarchive = () => {
    dispatch(
      rentSetupActiveInactiveAction(
        idToArchive?.id,
        idToArchive?.action,
        setLoading,
        setIsArchive,
        dispatch,
        (res) => {
          setSelectedItems([]);
          getRentsetupsList(rows, page, search, baseStatus);
        }
      )
    );
  };

  const archiveUnarchiveMultiple = (selectedItems, action) => {
    if (selectedItems?.length) {
      const selectedIds = selectedItems?.map((item) => {
        return item?.id;
      });
      setIdToArchive({ id: selectedIds, action: action });
      setIsArchive(true);
    } else {
      dispatch(
        showToastAction({
          type: "error",
          detail: "Please select at least one item.",
        })
      );
    }
  };

  return (
    <div>
      <CustomCard title="Rent Setup List" header={header()}>
        <CustomTableContainer
          list={rentSetupsList}
          columns={columns}
          actions={actions}
          // setColumns={setColumns}
          first={first}
          rows={rows}
          count={count}
          ActionTemplate={ActionTemplate}
          activeRow={rentSetup?.id}
          slice={setRentSetupSlice}
          handleSort={""}
          onPageChange={onPageChange}
          // columnOptions={columnOptions}
          // setColumnOptions={setColumnOptions}
          openAction={openAction}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          firstCol="Regulation Year"
        />
      </CustomCard>
      <CustomArchiveModal
        isOpen={isArchive}
        setIsOpen={setIsArchive}
        item={selectedItems?.length > 1 ? "rent setups" : "rent setup"}
        onSubmit={handleArchiveUnarchive}
        loading={loading}
        heading={baseStatus === "active" ? "Archive" : "Unarchive"}
      />
    </div>
  );
};

export default RentSetupList;
